import './mixpanel-loader'

import { createPromise } from '@eturi/util'
import { SessionDeviceId } from '../env'
import type { RootState } from '../reducers/rootReducer'

// Enable dev logging of analytics to testing setup
const ENABLE_DEV_LOGGING = process.env.APP_ENV == 'dev'

// Enable actual tracking in dev for testing events
const ENABLE_DEV_TRACKING = false

// Whether we should track. By default, we won't track in development mode, and always track
// in QA / Prod. Dev tracking can be enabled above for testing.
const SHOULD_TRACK =
	process.env.E2E != '1' && (ENABLE_DEV_TRACKING || process.env.NODE_ENV != 'development')

const log = (name: string, ...args: any[]) =>
	ENABLE_DEV_LOGGING && console.log(`[Analytics ${name}]`, ...args)

type StdAnalyticsCtx = {
	readonly account_id?: string
	readonly app_version: string
	readonly created?: number
	readonly device_id?: string
}

const mp = () => window.mixpanel

const stdAnalyticsCtx$ = ({ account }: RootState): StdAnalyticsCtx => ({
	account_id: account.account?.account_id,
	app_version: process.env.APP_VERSION,
	created: account.account?.created,
	device_id: SessionDeviceId.get(),
})

const mixpanelLoadedPromiseObj = createPromise()

export const initAnalytics = () => {
	if (process.env.E2E == '1') return

	mp().init(process.env.OURPACT_MIXPANEL_TOKEN, {
		api_host: process.env.OURPACT_MIXPANEL_URL,
		debug: ENABLE_DEV_LOGGING && ENABLE_DEV_TRACKING,
		loaded: () => {
			mixpanelLoadedPromiseObj.resolve()
		},
		ignore_dnt: true,
		persistence: 'localStorage',
	})
}

export const trackWithState = (state: RootState, type: string, data?: Record<string, any>) => {
	try {
		const mixpanelProps = mergeAnalyticsProps(
			data,
			stdAnalyticsCtx$(state),
			state.fflags?.flags?.feature_set_props,
		)

		log('Track', type, mixpanelProps)

		if (!SHOULD_TRACK) return

		mp().track(type, mixpanelProps)
	} catch {
		// Nai
	}
}

export const getDistinctId = () =>
	mixpanelLoadedPromiseObj.promise.then((): string => mp().get_distinct_id())

export const analyticsLogout = () => {
	log('Logout')

	if (!SHOULD_TRACK) return

	mp().reset()
}

export const identify = async (accountId: string) => {
	log('Identify')

	if (!SHOULD_TRACK) return

	const id = await getDistinctId()
	return id === accountId ? undefined : mp().identify(accountId)
}

export const timeEvent = (name: string) => {
	log('Timing', `: ${name}`)

	if (!SHOULD_TRACK) return

	mp().time_event(name)
}

const mergeAnalyticsProps = (...propsObjects: Maybe<Record<string, unknown>>[]) => {
	const mergedProps: Record<string, unknown> = {}

	for (const obj of propsObjects) {
		if (!obj) continue

		for (const key in obj) {
			const value = obj[key]
			if (value !== undefined) mergedProps[key] = value
		}
	}

	return mergedProps
}
